import invariant from 'invariant';
import path from "ramda/es/path";
import prop from "ramda/es/prop";
import { useEffect } from 'react';
import { ReplaySubject, zip } from 'rxjs';
import { first, map as rxMap } from 'rxjs/operators';
import { getDeepPath } from '../../utils/data';
import { getFieldDescriptorWithInvariant } from '../utils';
const streamMap = {};
/**
 * whole streamStorage$ as stream
 *
 * @documented
 * @category StreamStorage
 * @import import { streamStorage$ } from '@gef-ui/form-core/stream/streamStorage'
 * @example
 *
 * 	streamStorage$.subscribe((streamStorage) => console.log(streamStorage))
 *
 */ export const streamStorage$ = new ReplaySubject(1);
/**
 * get formStream$ for given form. Return empty object if not found.
 *
 * @documented
 * @category StreamStorage
 * @import import { getFormStream } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @example
 *
 * 	getFormStream('example')
 *
 */ export const getFormStream = (formName)=>streamMap[formName] || {};
const getFormStreamWithInvariant = (formName)=>{
    const formStream$ = streamMap[formName];
    invariant(formStream$, `cannot find stream for ${formName}`);
    return formStream$;
};
/**
 * get formData for given form
 *
 * @documented
 * @category StreamStorage
 * @import import { getFormData } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @example
 *
 * 	getFormData('example')
 *
 */ export const getFormData = (formName)=>getFormStreamWithInvariant(formName).formData$.pipe(first()).toPromise();
/**
 * get globalErrors for given form
 *
 * @documented
 * @category StreamStorage
 * @import import { getGlobalErrors } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @example
 *
 * 	getGlobalErrors('example')
 *
 */ export const getGlobalErrors = (formName)=>getFormStreamWithInvariant(formName).globalErrors$.pipe(first()).toPromise();
/**
 * get form field state for given form
 *
 * @documented
 * @category StreamStorage
 * @import import { getFormFieldState } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @example
 *
 * 	getFormFieldState('example')
 *
 */ export const getFormFieldState = (formName)=>getFormStreamWithInvariant(formName).formFieldState$.pipe(first()).toPromise();
/**
 * get form state for given form
 *
 * @documented
 * @category StreamStorage
 * @import import { getFormState } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @example
 *
 * 	getFormState('example')
 *
 */ export const getFormState = (formName)=>getFormStreamWithInvariant(formName).formState$.pipe(first()).toPromise();
/**
 * get form pristine state value for given form
 *
 * @documented
 * @category StreamStorage
 * @import import { getIsFormPristine } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @example
 *
 * 	getIsFormPristine('example')
 *
 */ export const getIsFormPristine = (formName)=>getFormStreamWithInvariant(formName).formState$.pipe(first(), rxMap(prop('pristine'))).toPromise();
/**
 * get field value for given form
 *
 * @documented
 * @category StreamStorage
 * @import import { getFormFieldValue } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @param {String} fieldName
 * @param {Array} fieldIndices
 * @example
 *
 * 	getFormFieldValue({ formName: 'example', fieldName: 'name', fieldIndices: [0] })
 *
 */ export const getFormFieldValue = ({ formName, fieldName, fieldIndices })=>{
    const formStreams = getFormStreamWithInvariant(formName);
    return zip(formStreams.formData$, formStreams.formDescriptor$).pipe(rxMap(([formData, formDescriptor])=>{
        const fieldDescriptor = getFieldDescriptorWithInvariant(formDescriptor, fieldName);
        return path(getDeepPath(formDescriptor, fieldDescriptor, fieldIndices), formData);
    }), first()).toPromise();
};
/**
 * send action to given form control
 *
 * @documented
 * @category StreamStorage
 * @import import { sendControlNext } from '@gef-ui/form-core/stream/streamStorage'
 * @param {String} formName
 * @param {String} action
 * @example
 *
 * 	sendControlNext('example', {type: 'SOME_ACTION'})
 *
 */ export const sendControlNext = (formName, action)=>getFormStreamWithInvariant(formName).control$.next(action);
export const useSaveStreamStorage = (formStream$, { name: formName })=>{
    useEffect(()=>{
        invariant(!streamMap[formName], `stream already stored for ${formName}`);
        streamMap[formName] = formStream$;
        streamStorage$.next(streamMap);
        return ()=>{
            streamMap[formName] = undefined;
            streamStorage$.next(streamMap);
        };
    }, [
        formStream$,
        formName
    ]);
};
